import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Code2, 
  Server, 
  Database, 
  Shield,
  Settings
} from 'lucide-react';

const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const skillCategories = [
    {
      title: "Frontend & Mobile",
      icon: <Code2 size={24} />,
      skills: ["React", "React Native", "JavaScript", "TypeScript", "HTML/CSS"],
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      title: "Backend & API", 
      icon: <Server size={24} />,
      skills: ["Go (Gin)", "REST APIs", "Swagger Documentation", "Microservices"],
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      title: "Databases",
      icon: <Database size={24} />,
      skills: ["MySQL", "PostgreSQL", "Redis", "SQLite", "GORM"],
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      title: "DevOps & Infrastructure",
      icon: <Shield size={24} />,
      skills: ["Docker", "Docker Compose", "Proxmox", "Cloud-init", "Nginx", "CI/CD", "Shell Scripting"],
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
    },
    {
      title: "Security & Tools",
      icon: <Settings size={24} />,
      skills: ["OAuth & JWT", "SSL/TLS", "Cloudflare", "WAF", "Git", "Firebase", "Postman"],
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const skillVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="skills" className="skills section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            className="section-title"
            variants={skillVariants}
          >
            Skills & Technologies
          </motion.h2>
          
          <div className="skills-grid">
            {skillCategories.map((category, index) => (
              <motion.div
                key={category.title}
                className="skill-category"
                variants={skillVariants}
                whileHover={{ 
                  y: -10,
                  rotateX: -5,
                  scale: 1.03,
                  transition: { duration: 0.3 }
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                  e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.setProperty('--mouse-x', '50%');
                  e.currentTarget.style.setProperty('--mouse-y', '50%');
                }}
              >
                <div 
                  className="skill-gradient-bg"
                  style={{ background: category.gradient }}
                ></div>
                <motion.div 
                  className="skill-icon"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 10,
                    transition: { duration: 0.4 }
                  }}
                >
                  {category.icon}
                </motion.div>
                
                <h3 className="skill-title">{category.title}</h3>
                
                <ul className="skill-list">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.li
                      key={skillIndex}
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {skill}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
